import * as React from "react"
import { ExternalLink, Loader2, RotateCcw, Search, UserPlus, UserX, Undo2 } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ServerPagination } from "@/components/ui/server-pagination"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDate } from "@/lib/date-time"
import { cn } from "@/lib/utils"
import { EMPLOYEE_PARAM } from "@/pages/employees/url-params"
import {
  decideEmployee,
  fetchEmployees,
  type HrisConnection,
  type HrisEmployeeFilter,
  type HrisEmployeeRow,
  type HrisEmployeesPage,
} from "@/features/integrations/hris/hrisApi"

type Props = {
  connection: HrisConnection
  initialFilter?: HrisEmployeeFilter
}

const FILTERS: HrisEmployeeFilter[] = ["attention", "new", "synced", "pending", "skipped", "archived", "all"]

/** Reasons the customer can fix by adding the employee with a company and/or email. */
const FIXABLE_REASONS = ["legal_entity_not_mapped", "company_not_in_account", "no_email", "duplicate_email_in_hris", "email_taken_other_account"]

/** Reasons fixed by typing another email. */
const EMAIL_REASONS = ["no_email", "duplicate_email_in_hris", "email_taken_other_account"]

function useFilterLabels() {
  const t = useBookingT()
  return React.useMemo<Record<HrisEmployeeFilter, string>>(
    () => ({
      attention: t("hris_f_attention", "Need attention"),
      new: t("hris_f_new", "New (30 days)"),
      synced: t("hris_f_synced", "In Travel Code"),
      pending: t("hris_f_pending", "Will be added"),
      skipped: t("hris_f_skipped", "Not synced"),
      archived: t("hris_f_archived", "Archived"),
      all: t("hris_f_all", "All"),
    }),
    [t],
  )
}

function StateBadge({ row }: { row: HrisEmployeeRow }) {
  const t = useBookingT()
  if (row.state === "attention") return <Badge variant="warning">{t("hris_f_attention", "Need attention")}</Badge>
  if (row.state === "synced") return <Badge variant="green">{t("hris_state_synced", "Synced")}</Badge>
  if (row.state === "pending") return <Badge variant="secondary">{t("hris_f_pending", "Will be added")}</Badge>
  if (row.state === "archived") return <Badge variant="frozen">{t("hris_f_archived", "Archived")}</Badge>
  return <Badge variant="frozen">{t("hris_f_skipped", "Not synced")}</Badge>
}

/**
 * The HR system's employees by where they stand, with the customer's say over
 * the ones the sync could not place: add (choosing the company or typing the
 * email the HR system lacks) or skip for good. Layout follows the list-page
 * pattern: summary tiles, a filter card, a card with only the table.
 */
export function HrisEmployeesView({ connection, initialFilter }: Props) {
  const t = useBookingT()
  const labels = useFilterLabels()
  const [filter, setFilter] = React.useState<HrisEmployeeFilter | null>(initialFilter ?? null)
  const [query, setQuery] = React.useState("")
  const [debounced, setDebounced] = React.useState("")
  const [page, setPage] = React.useState(1)
  const [data, setData] = React.useState<HrisEmployeesPage | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [adding, setAdding] = React.useState<HrisEmployeeRow | null>(null)
  const [busyId, setBusyId] = React.useState<string | null>(null)

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [query])

  const effectiveFilter: HrisEmployeeFilter = filter ?? "attention"

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchEmployees(connection.id, { tab: effectiveFilter, q: debounced, page })
      if (filter === null && res.counts.attention === 0 && effectiveFilter === "attention") {
        setFilter("all")
        return
      }
      setData(res)
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [connection.id, debounced, effectiveFilter, filter, page])

  React.useEffect(() => {
    void load()
  }, [load])

  const decide = async (row: HrisEmployeeRow, decision: "include" | "skip" | "reset", extra: { companyId?: number | null; email?: string | null } = {}) => {
    setBusyId(row.externalId)
    try {
      const res = await decideEmployee(connection.id, row.externalId, { decision, ...extra })
      if (decision === "skip") {
        toast.success(t("hris_skipped_toast", "{name} will not be synced.").replace("{name}", row.name), {
          action: { label: t("hris_undo", "Undo"), onClick: () => void decide(res.employee, "reset") },
        })
      } else if (decision === "include") {
        toast.success(
          res.run
            ? t("hris_added_toast_sync", "{name} will be added: a sync has started.").replace("{name}", row.name)
            : t("hris_added_toast", "{name} will be added with the next sync.").replace("{name}", row.name),
        )
      }
      await load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusyId(null)
    }
  }

  const activeCount = (filter !== null && filter !== "all" ? 1 : 0) + (debounced !== "" ? 1 : 0)
  const counts = data?.counts

  const tiles: { key: HrisEmployeeFilter; tone: string }[] = [
    { key: "attention", tone: "text-warning-700" },
    { key: "new", tone: "text-success-700" },
    { key: "synced", tone: "text-neutral-900" },
    { key: "pending", tone: "text-neutral-900" },
    { key: "skipped", tone: "text-neutral-900" },
    { key: "archived", tone: "text-neutral-900" },
  ]

  const rowDetail = (row: HrisEmployeeRow): string =>
    row.error ??
    row.reasonLabel ??
    (row.syncedAt
      ? t("hris_synced_on", "Added on {date}").replace("{date}", formatUserDate(row.syncedAt))
      : row.state === "synced"
        ? t("hris_synced_existing", "Matched to an existing Travel Code account")
        : "—")

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-3 max-xl:grid-cols-3 max-md:grid-cols-2">
        {tiles.map(({ key, tone }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setFilter(key)
              setPage(1)
            }}
            className={cn(
              "rounded-lg border bg-white p-4 text-left transition-colors hover:border-brand-500",
              effectiveFilter === key ? "border-brand-500" : "border-neutral-200",
            )}
          >
            <div className="text-xs uppercase tracking-wide text-neutral-800">{labels[key]}</div>
            <div className={cn("mt-1 text-2xl font-semibold", tone)}>{counts ? counts[key] : "…"}</div>
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4 max-md:px-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
              {t("hris_filters", "Filters")}
              {activeCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2.5 py-0.5 text-xs font-medium text-white">
                  {activeCount}
                  <span className="font-normal">{t("hris_active", "active")}</span>
                </span>
              )}
            </div>
            {activeCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilter("all")
                  setQuery("")
                  setPage(1)
                }}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                {t("hris_reset", "Reset all")}
              </Button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 xl:grid-cols-7">
            <div className="space-y-1 lg:col-span-2">
              <Label className="text-xs text-neutral-800">{t("hris_search", "Name or email")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                <Input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setPage(1)
                  }}
                  className="h-9 pl-9"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-neutral-800">{t("hris_status", "Status")}</Label>
              <Select
                value={effectiveFilter}
                onValueChange={(v) => {
                  setFilter(v as HrisEmployeeFilter)
                  setPage(1)
                }}
              >
                <SelectTrigger className="h-9 min-w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILTERS.map((key) => (
                    <SelectItem key={key} value={key}>
                      {labels[key]}
                      {counts ? ` (${counts[key]})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-neutral-800">
            {t("hris_found", "Found: {count}").replace("{count}", String(data?.meta.total ?? 0))}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : data && data.items.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-800">{t("hris_empty", "No employees here.")}</p>
          ) : (
            <>
              {/* Phones: one card per employee, so details and actions stay on screen. */}
              <ul className="divide-y divide-neutral-200 md:hidden">
                {data?.items.map((row) => (
                  <li key={row.externalId} className="space-y-2 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-neutral-900">{row.name}</div>
                        <div className="break-all text-sm text-neutral-800">{row.email ?? t("hris_no_email", "no email")}</div>
                      </div>
                      <StateBadge row={row} />
                    </div>
                    {row.department && <div className="text-sm text-neutral-800">{row.department}</div>}
                    <div className="text-sm text-neutral-800">{rowDetail(row)}</div>
                    <RowActions row={row} busy={busyId === row.externalId} onAdd={() => setAdding(row)} onDecide={(d) => void decide(row, d)} />
                  </li>
                ))}
              </ul>
            <Table className="max-md:hidden">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("hris_col_employee", "Employee")}</TableHead>
                  <TableHead className="max-lg:hidden">{t("hris_col_legal_entity", "Legal entity")}</TableHead>
                  <TableHead className="max-xl:hidden">{t("hris_col_department", "Department")}</TableHead>
                  <TableHead>{t("hris_col_status", "Status")}</TableHead>
                  <TableHead>{t("hris_col_details", "Details")}</TableHead>
                  <TableHead className="text-right">{t("hris_col_actions", "Actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.map((row) => (
                  <TableRow key={row.externalId}>
                    <TableCell>
                      <div className="text-sm font-medium text-neutral-900">{row.name}</div>
                      <div className="text-sm text-neutral-800">{row.email ?? t("hris_no_email", "no email")}</div>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-800 max-lg:hidden">{row.legalEntity ?? "—"}</TableCell>
                    <TableCell className="text-sm text-neutral-800 max-xl:hidden">{row.department ?? "—"}</TableCell>
                    <TableCell>
                      <StateBadge row={row} />
                    </TableCell>
                    <TableCell className="text-sm text-neutral-800">{rowDetail(row)}</TableCell>
                    <TableCell className="text-right">
                      <RowActions row={row} busy={busyId === row.externalId} onAdd={() => setAdding(row)} onDecide={(d) => void decide(row, d)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
          {data && data.meta.total > data.meta.perPage && (
            <ServerPagination page={data.meta.page} pageSize={data.meta.perPage} total={data.meta.total} onPageChange={setPage} hidePageSize />
          )}
        </CardContent>
      </Card>

      <AddEmployeeSheet
        row={adding}
        companies={data?.companies ?? []}
        onClose={() => setAdding(null)}
        onSubmit={async (companyId, email) => {
          if (!adding) return
          const row = adding
          setAdding(null)
          await decide(row, "include", { companyId, email })
        }}
      />
    </div>
  )
}

function RowActions({
  row,
  busy,
  onAdd,
  onDecide,
}: {
  row: HrisEmployeeRow
  busy: boolean
  onAdd: () => void
  onDecide: (decision: "skip" | "reset") => void
}) {
  const t = useBookingT()
  if (busy) return <Loader2 className="ml-auto size-4 animate-spin text-brand-500" />

  if (row.decision === "skip") {
    return (
      <Button variant="ghost" size="sm" onClick={() => onDecide("reset")}>
        <Undo2 className="size-4" />
        {t("hris_unskip", "Sync again")}
      </Button>
    )
  }

  const canAdd = row.state === "attention" && row.reason !== null && FIXABLE_REASONS.includes(row.reason)
  const canSkip = row.state === "attention" || row.state === "pending"

  return (
    <div className="flex flex-wrap justify-end gap-1 max-md:justify-start">
      {row.userId !== null && row.state === "synced" && (
        <Button variant="ghost" size="sm" asChild>
          <a href={`/app/profiles?${EMPLOYEE_PARAM}=${row.userId}`}>
            <ExternalLink className="size-4" />
            {t("hris_open_profile", "Profile")}
          </a>
        </Button>
      )}
      {canAdd && (
        <Button variant="outline" size="sm" onClick={onAdd}>
          <UserPlus className="size-4" />
          {t("hris_add", "Add")}
        </Button>
      )}
      {canSkip && (
        <Button variant="ghost" size="sm" onClick={() => onDecide("skip")}>
          <UserX className="size-4" />
          {t("hris_skip", "Skip")}
        </Button>
      )}
    </div>
  )
}

function AddEmployeeSheet({
  row,
  companies,
  onClose,
  onSubmit,
}: {
  row: HrisEmployeeRow | null
  companies: { id: number; name: string }[]
  onClose: () => void
  onSubmit: (companyId: number | null, email: string | null) => Promise<void>
}) {
  const t = useBookingT()
  const [companyId, setCompanyId] = React.useState<string>("")
  const [email, setEmail] = React.useState("")
  const needsEmail = !row?.email || (row?.reason !== null && row?.reason !== undefined && EMAIL_REASONS.includes(row.reason))
  const needsCompany = row?.reason === "legal_entity_not_mapped" || row?.reason === "company_not_in_account"

  React.useEffect(() => {
    setCompanyId(row?.decisionCompany ? String(row.decisionCompany) : "")
    setEmail(row?.decisionEmail ?? "")
  }, [row])

  const valid = (!needsCompany || companyId !== "") && (!needsEmail || /.+@.+\..+/.test(email.trim()))

  return (
    <Sheet open={row !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("hris_add_title", "Add {name}").replace("{name}", row?.name ?? "")}</SheetTitle>
          <SheetDescription>{row?.reasonLabel}</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          {row?.legalEntity && (
            <p className="text-sm text-neutral-800">
              {t("hris_add_legal_entity", "Legal entity in the HR system: {name}").replace("{name}", row.legalEntity)}
            </p>
          )}
          <div className="space-y-2">
            <Label>{t("hris_add_company", "Company in Travel Code")}</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger>
                <SelectValue placeholder={needsCompany ? t("hris_add_company_ph", "Choose a company") : t("hris_add_company_keep", "As mapped")} />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={String(company.id)}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {needsEmail && (
            <div className="space-y-2">
              <Label htmlFor="hris-add-email">{t("hris_add_email", "Work email")}</Label>
              <Input id="hris-add-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
              <p className="text-sm text-neutral-800">
                {row?.email
                  ? t("hris_add_email_hint_replace", "The email in the HR system cannot be used for this employee. Type the one to sign in with; the HR system is not changed.")
                  : t("hris_add_email_hint", "The HR system has no email for this employee. It is used to sign in and for trip notifications.")}
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            {t("hris_cancel", "Cancel")}
          </Button>
          <Button
            disabled={!valid}
            onClick={() => void onSubmit(companyId !== "" ? Number(companyId) : null, needsEmail ? email.trim() : null)}
          >
            {t("hris_add_submit", "Add")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
